import React, { useCallback, useEffect, useState } from "react";
import {
  getReferralsReceived,
  acceptReferral,
  declineReferral,
  requestMoreInfo,
  completeReferral,
} from "../APIs/referralApi";
import ReferralPacketModal from "./ReferralPacketModal";
import ReferralDocumentsSection from "./ReferralDocumentsSection";

const STATUS_CONFIG = {
  PENDING_ACCEPTANCE: {
    label: "Awaiting Your Response",
    color: "bg-yellow-100 text-yellow-700",
  },
  ACCEPTED: { label: "Accepted", color: "bg-green-100 text-green-700" },
  DECLINED: { label: "Declined", color: "bg-red-100 text-red-600" },
  INFO_REQUESTED: {
    label: "Info Requested",
    color: "bg-blue-100 text-blue-700",
  },
  MANUAL_REVIEW: {
    label: "Accepted — Awaiting Availability",
    color: "bg-orange-100 text-orange-700",
  },
  SCHEDULED: { label: "Booked", color: "bg-green-100 text-green-700" },
  COMPLETED: { label: "Visit Completed", color: "bg-gray-100 text-gray-500" },
  CANCELLED: {
    label: "Cancelled by Referring Provider",
    color: "bg-red-100 text-red-700",
  },
};

const URGENCY_LABEL = {
  ROUTINE: "Routine",
  WITHIN_30_DAYS: "Within 30 Days",
  WITHIN_7_DAYS: "Within 7 Days",
  WITHIN_3_DAYS: "Within 3 Days",
};

export default function ReferralsReceivedTab({
  providerId,
  statusFilter,
  declinedByMe,
  cancelledByPcp,
}) {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [packetModal, setPacketModal] = useState(null); // { referralId }
  const [actionState, setActionState] = useState({}); // { [referralId]: { mode, text, submitting } }
  const [completing, setCompleting] = useState({});
  const [confirmingComplete, setConfirmingComplete] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getReferralsReceived(providerId);
      setReferrals(Array.isArray(data) ? data : (data.content ?? []));
    } catch (err) {
      console.error("Failed to load received referrals:", err);
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    load();
  }, [load]);

  const getAction = (id) => actionState[id] ?? {};
  const setAction = (id, update) =>
    setActionState((s) => ({ ...s, [id]: { ...s[id], ...update } }));

  const handleAccept = async (r) => {
    setAction(r.referralId, { submitting: true });
    try {
      await acceptReferral(r.referralId, providerId);
      await load();
    } catch (err) {
      alert(err?.response?.data?.message || err.message || "Accept failed");
    } finally {
      setAction(r.referralId, { submitting: false });
    }
  };

  const handleDecline = async (r) => {
    setAction(r.referralId, { submitting: true });
    try {
      await declineReferral(
        r.referralId,
        providerId,
        getAction(r.referralId).text ?? "",
      );
      setAction(r.referralId, { mode: null, text: "" });
      await load();
    } catch (err) {
      alert(err?.response?.data?.message || err.message || "Decline failed");
      setAction(r.referralId, { submitting: false });
    }
  };

  const handleRequestInfo = async (r) => {
    const note = getAction(r.referralId).text ?? "";
    if (!note.trim()) return;
    setAction(r.referralId, { submitting: true });
    try {
      await requestMoreInfo(r.referralId, providerId, note);
      setAction(r.referralId, { mode: null, text: "" });
      await load();
    } catch (err) {
      alert(err?.response?.data?.message || err.message || "Request failed");
      setAction(r.referralId, { submitting: false });
    }
  };

  const handleComplete = async (referralId) => {
    setCompleting((c) => ({ ...c, [referralId]: true }));
    setConfirmingComplete((c) => ({ ...c, [referralId]: false }));
    try {
      await completeReferral(referralId);
      await load();
    } catch (err) {
      alert(
        err?.response?.data?.message ||
          err.message ||
          "Failed to complete referral",
      );
    } finally {
      setCompleting((c) => ({ ...c, [referralId]: false }));
    }
  };

  if (loading)
    return (
      <p className="text-sm text-gray-500 py-6 text-center">
        Loading received referrals...
      </p>
    );

  const myAttempt = (r) =>
    r.matchAttempts?.find((a) => a.specialistProviderId === providerId);

  const visible = cancelledByPcp
    ? referrals.filter((r) => r.status === "CANCELLED")
    : declinedByMe
      ? referrals.filter((r) => {
          const attempt = myAttempt(r);
          if (attempt) return attempt.status === "DECLINED";
          return r.status === "DECLINED";
        })
      : statusFilter
        ? referrals.filter(
            (r) =>
              statusFilter.includes(r.status) &&
              myAttempt(r)?.status !== "DECLINED" &&
              r.status !== "CANCELLED",
          )
        : referrals.filter(
            (r) =>
              myAttempt(r)?.status !== "DECLINED" && r.status !== "CANCELLED",
          );

  if (visible.length === 0)
    return (
      <p className="py-12 text-center text-gray-500">No inbound referrals.</p>
    );

  return (
    <>
      <div className="space-y-3">
        {visible.map((r) => {
          const ac = getAction(r.referralId);
          const status = STATUS_CONFIG[r.status] ?? {
            label: r.status,
            color: "bg-gray-100 text-gray-600",
          };

          return (
            <div
              key={r.referralId}
              className="rounded-xl border bg-white shadow-sm overflow-hidden"
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-4">
                  {/* Left — stacked text */}
                  <div>
                    <div className="text-base font-semibold text-gray-900">
                      {r.patientName ?? `Patient #${r.patientId}`}
                    </div>
                    {r.referringProviderName && (
                      <div className="text-sm text-gray-500">
                        From: {r.referringProviderName}
                      </div>
                    )}
                    {r.specialty && (
                      <div className="text-sm text-gray-600">{r.specialty}</div>
                    )}
                    {r.reason && (
                      <div className="text-sm text-gray-500">
                        Reason: {r.reason}
                      </div>
                    )}
                    {r.urgency && (
                      <div className="text-sm text-gray-500">
                        Urgency: {URGENCY_LABEL[r.urgency] ?? r.urgency}
                      </div>
                    )}
                    {r.createdAt && (
                      <div className="text-sm text-gray-500">
                        Created:{" "}
                        {new Date(r.createdAt).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </div>
                    )}
                  </div>

                  {/* Right — status badge */}
                  <div className="shrink-0">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${status.color}`}
                    >
                      {status.label}
                    </span>
                  </div>
                </div>

                {/* Cancelled-by-PCP summary */}
                {r.status === "CANCELLED" && (
                  <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                    <span className="font-medium">
                      Cancelled by Referring Provider
                    </span>
                    {r.cancellationReason && (
                      <p className="mt-0.5">
                        <span className="font-medium">Reason:</span>{" "}
                        {r.cancellationReason}
                      </p>
                    )}
                  </div>
                )}

                {/* Declined-by-me summary */}
                {declinedByMe &&
                  (() => {
                    const a = myAttempt(r);
                    return a ? (
                      <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                        You declined this referral
                        {a.respondedAt
                          ? ` on ${new Date(a.respondedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
                          : ""}
                        {a.declineReason ? `. Reason: ${a.declineReason}` : ""}
                      </div>
                    ) : null;
                  })()}

                {/* Actions */}
                {r.status === "PENDING_ACCEPTANCE" &&
                  ac.mode == null &&
                  !declinedByMe && (
                    <div className="flex gap-2 mt-3 flex-wrap">
                      <button
                        onClick={() =>
                          setPacketModal({
                            referralId: r.referralId,
                            status: r.status,
                          })
                        }
                        className="text-xs px-3 py-1.5 rounded border hover:bg-gray-50"
                      >
                        View Packet
                      </button>
                      <button
                        disabled={ac.submitting}
                        onClick={() => handleAccept(r)}
                        className="text-xs px-3 py-1.5 rounded bg-green-600 text-white disabled:opacity-40"
                      >
                        {ac.submitting ? "..." : "Accept"}
                      </button>
                      <button
                        onClick={() =>
                          setAction(r.referralId, { mode: "decline" })
                        }
                        className="text-xs px-3 py-1.5 rounded bg-red-100 text-red-700 hover:bg-red-200"
                      >
                        Decline
                      </button>
                      <button
                        onClick={() =>
                          setAction(r.referralId, { mode: "info" })
                        }
                        className="text-xs px-3 py-1.5 rounded bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                      >
                        Request More Info
                      </button>
                    </div>
                  )}

                {(r.status !== "PENDING_ACCEPTANCE" || declinedByMe) && (
                  <button
                    onClick={() =>
                      setPacketModal({
                        referralId: r.referralId,
                        status: declinedByMe ? "DECLINED" : r.status,
                      })
                    }
                    className="text-xs px-3 py-1.5 rounded border hover:bg-gray-50 mt-3"
                  >
                    View Packet
                  </button>
                )}

                {r.status === "SCHEDULED" &&
                  !declinedByMe &&
                  !cancelledByPcp && (
                    <div className="flex items-center gap-3 mt-3">
                      {confirmingComplete[r.referralId] ? (
                        <>
                          <span className="text-xs text-gray-600">
                            Mark this referral as completed?
                          </span>
                          <button
                            onClick={() => handleComplete(r.referralId)}
                            disabled={completing[r.referralId]}
                            className="text-xs px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-40"
                          >
                            {completing[r.referralId]
                              ? "Completing..."
                              : "Yes, Complete"}
                          </button>
                          <button
                            onClick={() =>
                              setConfirmingComplete((c) => ({
                                ...c,
                                [r.referralId]: false,
                              }))
                            }
                            className="text-xs px-3 py-1.5 rounded border"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() =>
                            setConfirmingComplete((c) => ({
                              ...c,
                              [r.referralId]: true,
                            }))
                          }
                          className="text-xs px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700"
                        >
                          Complete Referral
                        </button>
                      )}
                    </div>
                  )}

                {/* Decline inline */}
                {ac.mode === "decline" && !declinedByMe && (
                  <div className="mt-3 space-y-2">
                    <textarea
                      className="w-full border rounded px-3 py-2 text-sm"
                      rows={2}
                      placeholder="Reason for declining (optional)..."
                      value={ac.text ?? ""}
                      onChange={(e) =>
                        setAction(r.referralId, { text: e.target.value })
                      }
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => setAction(r.referralId, { mode: null })}
                        className="text-xs px-3 py-1.5 rounded border"
                      >
                        Cancel
                      </button>
                      <button
                        disabled={ac.submitting}
                        onClick={() => handleDecline(r)}
                        className="text-xs px-3 py-1.5 rounded bg-red-600 text-white disabled:opacity-40"
                      >
                        {ac.submitting ? "Declining..." : "Confirm Decline"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Request info inline */}
                {ac.mode === "info" && !declinedByMe && (
                  <div className="mt-3 space-y-2">
                    <textarea
                      className="w-full border rounded px-3 py-2 text-sm"
                      rows={2}
                      placeholder="What info do you need from the referring provider?"
                      value={ac.text ?? ""}
                      onChange={(e) =>
                        setAction(r.referralId, { text: e.target.value })
                      }
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => setAction(r.referralId, { mode: null })}
                        className="text-xs px-3 py-1.5 rounded border"
                      >
                        Cancel
                      </button>
                      <button
                        disabled={ac.submitting || !ac.text?.trim()}
                        onClick={() => handleRequestInfo(r)}
                        className="text-xs px-3 py-1.5 rounded bg-yellow-500 text-white disabled:opacity-40"
                      >
                        {ac.submitting ? "Sending..." : "Send Request"}
                      </button>
                    </div>
                  </div>
                )}

                <ReferralDocumentsSection referralId={r.referralId} />
              </div>
            </div>
          );
        })}
      </div>

      {packetModal && (
        <ReferralPacketModal
          referralId={packetModal.referralId}
          specialistProviderId={providerId}
          status={packetModal.status}
          onClose={() => setPacketModal(null)}
          onActionDone={load}
        />
      )}
    </>
  );
}
